// Runtime loader for the precomputed ELK LR layout.
// Re-runs at build time via scripts/build-graph-layout.mjs (npm run build:layout).
//
// Visual constants are co-located here so every beat reads from one source.

import layoutJson from "./graph-layout.json";
import { SVG_COLORS } from "@/lib/svg-colors";

export interface LayoutPoint { x: number; y: number; }

export interface LayoutNodeV2 {
  id: string;
  label: string;
  tier: number;
  x: number;
  y: number;
  w: number;
  h: number;
  cx: number;
  cy: number;
}

export interface LayoutEdgeV2 {
  id: string;
  from: string;
  to: string;
  points: LayoutPoint[];
}

export interface LayoutDoc {
  viewBox: { w: number; h: number };
  nodes: LayoutNodeV2[];
  edges: LayoutEdgeV2[];
}

export const LAYOUT: LayoutDoc = layoutJson;

export const NODES_BY_ID: ReadonlyMap<string, LayoutNodeV2> = new Map(
  LAYOUT.nodes.map((n) => [n.id, n] as const),
);

export function getNode(id: string): LayoutNodeV2 {
  const n = NODES_BY_ID.get(id);
  if (!n) throw new Error(`graph-layout: unknown node id "${id}"`);
  return n;
}

// Bbox of a set of nodes — used to anchor cards/zones.
export function bboxOf(ids: readonly string[]): { x: number; y: number; w: number; h: number } {
  const ns = ids.map(getNode);
  const minX = Math.min(...ns.map((n) => n.x));
  const minY = Math.min(...ns.map((n) => n.y));
  const maxX = Math.max(...ns.map((n) => n.x + n.w));
  const maxY = Math.max(...ns.map((n) => n.y + n.h));
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

// Convert an edge polyline to an SVG path string (rounded corners via stroke-linejoin).
export function polylinePath(points: LayoutPoint[]): string {
  if (points.length === 0) return "";
  // points is non-empty (guard above); [0] is always defined.
  const first = points[0]!;
  const head = `M${first.x},${first.y}`;
  const tail = points.slice(1).map((p) => `L${p.x},${p.y}`).join(" ");
  return tail ? `${head} ${tail}` : head;
}

// ───────────────────────────────────────────────────────────────────────────
// Visual constants — co-located so every beat reads from one source.
// All hex values map to design tokens in app/app.css. SVG attrs cannot use CSS
// vars during SSR (vars don't resolve in attribute strings server-side), so
// hex literals are duplicated here. Keep in sync with app/app.css :root.
// ───────────────────────────────────────────────────────────────────────────

export const TIER_LABELS = ["INGRESS", "COMPUTE", "AI / ML", "DATA", "BACKUPS"] as const;
export type TierIndex = 0 | 1 | 2 | 3 | 4;

export const EDGE_GAP = 24;
export const NODE_GAP = 16;
export const ZONE_PAD = 36;

// Node card geometry — single uniform card across all nodes.
export const NODE_CARD = {
  width:  150,
  height: 62,
  radius: 6,
  paddingX: 12,
  paddingY: 10,
  iconSize: 20,
  iconGap:  8,    // px between icon and service-name
  rowGap:   14,   // px between (icon + name) row and resource-id row
  shadow: "0 1px 2px rgba(17,22,29,0.05)",
} as const;

// Dash vocabulary — exactly three patterns are allowed.
export const DASH = {
  structural: "4 4",   // tier dividers, workload zone outline, "affected" node strokes
  deferred:   "4 3",   // copy edges (DDB→snap, RDS→snap), drift edge (lambda→bedrock)
  leader:     "2 3",   // leader lines from modal anchor to lit element
} as const;

// Tier dividers — committed structural element, not a whisper. Labels read
// as headings; dividers as light scaffolding rather than placeholder text.
export const DIVIDER = {
  stroke:      "rgba(74,83,97,0.32)",   // --foreground-muted at 0.32 alpha
  strokeWidth: 1,
  dash:        DASH.structural,
  labelColor:  SVG_COLORS.dividerLabel,  // a touch darker than foreground-muted for label confidence
  labelSize:   13,
  labelWeight: 600,
  labelTracking: "0.12em",
} as const;

// Scan tint — full-canvas sage overlay during beat 2 (data discovery phase).
export const SCAN_TINT_FILL = "rgba(47,107,99,0.08)";

// Workload zone (sage outline + very subtle sage tint)
export const ZONE = {
  stroke:      SVG_COLORS.primary,       // --primary
  strokeWidth: 1.5,                      // bumped from 1.25 so all 4 edges read evenly
  fill:        "rgba(47,107,99,0.05)",
  dash:        DASH.structural,
  labelColor:  SVG_COLORS.primary,
  radius:      10,
} as const;

// Drift uses red, finding uses warning yellow — distinct visual languages.
export const DRIFT_STROKE   = SVG_COLORS.risk;
export const DRIFT_TEXT     = SVG_COLORS.driftText;   // AA-safe on white
export const FINDING_STROKE = SVG_COLORS.drift;   // warning yellow

// Node card visual states
export const NODE_STATE = {
  default: { stroke: SVG_COLORS.border, width: 1,    dash: undefined },                       // --border
  focus:   { stroke: FINDING_STROKE,   width: 1.5,  dash: undefined },                       // beat-6 lit DDB (yellow)
  drift:   { stroke: DRIFT_STROKE,     width: 1.5,  dash: undefined },                       // bedrock (red)
  affected:{ stroke: FINDING_STROKE,   width: 1.25, dash: DASH.structural, opacity: 0.55 }, // ddb-snap in beat 6
} as const;

export type NodeState = keyof typeof NODE_STATE;

// Edges
export const EDGE = {
  flow:         "rgba(74,83,97,0.40)",   // --foreground-muted at 0.4 alpha
  flowWidth:    1.25,
  flowLit:      FINDING_STROKE,           // beat-6 DDB → DDB Snapshot lit (yellow)
  flowLitWidth: 1.5,
  driftStroke:  DRIFT_STROKE,             // beat-5 lambda → bedrock drift edge (red)
  // copyDash and driftDash both use DASH.deferred — intentionally same token.
  copyDash:  DASH.deferred,
  driftDash: DASH.deferred,
} as const;

// Modal accents
export const MODAL = {
  accent: {
    ok:      SVG_COLORS.primary,       // --primary
    warning: FINDING_STROKE,           // finding modal (yellow)
    drift:   DRIFT_STROKE,             // drift modal (red)
  },
  eyebrow: {
    ok:      SVG_COLORS.primary,
    warning: SVG_COLORS.warningText,   // warning text — AA-safe on white
    drift:   DRIFT_TEXT,
  },
  title:        SVG_COLORS.foreground,
  body:         SVG_COLORS.neutral600,
  surface:      SVG_COLORS.surface,
  border:       SVG_COLORS.border,
  shadow:       "0 2px 8px rgba(17,22,29,0.08)",
  radius:       8,
  paddingX:     16,
  paddingY:     14,
} as const;

// Chips
export const CLASSIFICATION_CHIP = {
  stroke: SVG_COLORS.neutral300,   // neutral-300
  text:   SVG_COLORS.neutral600,   // --foreground-muted
  fill:   "transparent",
} as const;

export const FINDING_CHIP = {
  bar:    SVG_COLORS.drift,        // --warning
  stroke: SVG_COLORS.neutral300,
  text:   SVG_COLORS.foreground,
  fill:   "transparent",
} as const;

// Leader lines
export const LEADER = {
  stroke: "rgba(74,83,97,0.40)",
  width:  1,
  dash:   DASH.leader,
} as const;

// Node text colors
export const NODE_TEXT = {
  name:   SVG_COLORS.foreground,  // --foreground
  meta:   SVG_COLORS.neutral600,  // --foreground-muted
  region: SVG_COLORS.primary,     // --primary
} as const;

// Dim opacity for off-focus elements at beats 5–6.
// Paper canvas has less contrast headroom than ink; 0.50 keeps any text legible.
// Dim multiplier for off-focus cards/edges at beats 5–6.
// Was 0.50 — too transparent; the dot-grid + workload zone behind the cards
// bled through. 0.75 keeps cards readable but visibly de-emphasized.
export const DIM_OPACITY = 0.75;

// Featured workload composition. Bedrock is NOT a workload member — it's drift.
export const FEATURED_WORKLOAD_NODES = [
  "api-gw", "lambda",
  "dynamo", "s3",
  "ddb-snap",
] as const;

// Per-node identifiers (resource id) — illustrative AWS-plausible names.
// Used by NodeCard. SSR-deterministic; never randomized.
export const NODE_META: Record<string, { resourceId: string; service: string }> = {
  "api-gw":   { service: "API Gateway",  resourceId: "/orders/v1" },
  "lambda":   { service: "Lambda",       resourceId: "order-processor" },
  "bedrock":  { service: "Bedrock",      resourceId: "claude-3-haiku" },
  "dynamo":   { service: "DynamoDB",     resourceId: "tbl-orders-prod" },
  "s3":       { service: "S3",           resourceId: "orders-exports" },
  "ddb-snap": { service: "Snapshot",     resourceId: "14 manual · 142d" },
};

// Personal Data classification — every data store + its backup carries PII.
// Appears below the relevant nodes from beat 4 onward.
export const PERSONAL_DATA_NODES = ["dynamo", "s3", "ddb-snap"] as const;

// Mobile SVG layout — derived constants so modal positions stay in sync with
// the workload zone and viewBox without manual coordinate tweaking.
const _m = {
  viewBox: { w: 360, h: 700 },
  zone:    { x: 12, y: 32, w: 336, h: 500 },
  modal:   { margin: 22, gap: 14 },
} as const;

export const MOBILE = {
  ..._m,
  modal: {
    ..._m.modal,
    x: _m.modal.margin,
    w: _m.viewBox.w - _m.modal.margin * 2,
    y: _m.zone.y + _m.zone.h + _m.modal.gap,
  },
} as const;

// Beat-6 finding — only DDB has a surfaced finding.
export const FINDING_NODE = "dynamo" as const;
export const AFFECTED_NODE = "ddb-snap" as const;

// ---------------------------------------------------------------------------
// Compat shims — referenced by stacked-static.tsx (deleted at B16).
// Do not use in new code; use NODE_CARD, NODE_STATE, EDGE directly.
// ---------------------------------------------------------------------------
/** @deprecated use NODE_CARD + NODE_STATE */
export const NODE_STYLE = {
  radius: NODE_CARD.radius,
  fill:   SVG_COLORS.paper,   // --color-secondary (paper canvas fill for nodes)
  stroke: NODE_STATE.default.stroke,
} as const;

/** @deprecated use EDGE */
export const STROKE = {
  edge:        EDGE.flow,
  width:       EDGE.flowWidth,
  widthActive: EDGE.flowLitWidth,
} as const;
